import { Observable, Subscription, filter } from 'rxjs';
import { businessHours, businessStatus, tooltipTriggers } from 'src/app/common/function';
import { directionsUrl } from 'src/app/common/directions';
import { BusinessFilter } from 'src/app/models/register-business';
import { state, style, transition, animate, trigger } from '@angular/animations';
import { BusinessService } from 'src/app/services/business.service';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from './../../../services/loader.service';
import { RestaurantListFilter } from './../../../models/restaurant-details';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { RestaurantDetailModalComponent } from 'src/app/components/restaurant-detail-modal/restaurant-detail-modal.component';
import {
  RestaurantDetails,
  RestaurantListType,
} from 'src/app/models/restaurant-details';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { hasNativeLoginBridge } from 'src/app/shared/auth/auth-runtime-context';
import { takeUntil, debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-restaurant-detail-section',
  templateUrl: './restaurant-detail-section.component.html',
  styleUrls: ['./restaurant-detail-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [trigger('fadeIn', [
    state('hidden', style({ opacity: 0 })),
    state('visible', style({ opacity: 1 })),
    transition('hidden => visible', animate('100ms ease-in')),
  ])]
})
export class RestaurantDetailSectionComponent implements OnInit, OnDestroy, OnChanges {
  private destroy$ = new Subject<void>();
  private loadDataSubject = new Subject<void>();
  @Input() center: any;
  @Input() gridView: boolean = false;
  @Input() restaurantListType: RestaurantListType;
  @Input()
  set businessFilter(value) {
    if (value) {
      this._businessFilter = value;
      if (!this.lastListEpicenter) {
        this.lastListEpicenter = this.getFilterEpicenter(this._businessFilter);
      }
      if (this.restoredFromSession && this.restoredFilterKey) {
        const incomingKey = this.buildFilterKey(this._businessFilter, this.showOpen, this.showClose);
        if (incomingKey === this.restoredFilterKey) {
          return;
        }
        this.restoredFromSession = false;
      }
      if (!this.triedRestoreFromSession && (!this.businessService.filterDataObject || this.businessService.hasListingReturnPending())) {
        this.triedRestoreFromSession = true;
        const storedState = this.businessService.hydrateListingState();
        const storedFilterData = storedState?.filterDataObject;
        const storedMeta = storedState?.listMeta;
        const hasStoredList = Array.isArray(storedState?.restaurantDetailsList);
        if (hasStoredList && this.businessService.shouldRestoreListingState(storedState)) {
          const storedShowOpen = typeof storedFilterData?.showOpen === 'boolean'
            ? storedFilterData.showOpen
            : storedMeta?.showOpen;
          const storedShowClose = typeof storedFilterData?.showClose === 'boolean'
            ? storedFilterData.showClose
            : storedMeta?.showClose;
          const storedKey = this.buildFilterKey(storedFilterData?.filter, storedShowOpen, storedShowClose);
          const incomingKey = this.buildFilterKey(this._businessFilter, storedShowOpen, storedShowClose);
          if (storedKey && storedKey === incomingKey) {
            if (this.restoreListingFromSessionState()) {
              return;
            }
          }
        }
      }
      if (this.ignoreNextFilterReset) {
        const incomingKey = this.buildFilterKey(this._businessFilter, this.showOpen, this.showClose);
        this.ignoreNextFilterReset = false;
        if (this.restoredFilterKey && this.restoredFilterKey === incomingKey) {
          return;
        }
      }
      if (this.businessFilter.zoomLevel >= this.restaurantDetailsList[0]?.zoomLevel) {
        this.restaurantDetailsList = [];
        this.restaurantCache.clear();
      }
      this.currentPage = 0;
      if (this.restaurantListType) {
        this.loadDataSubject.next();
      }
    }
  }
  get businessFilter(): BusinessFilter {
    return this._businessFilter;
  }

  @Output() onListLoad = new EventEmitter<any>();

  @ViewChildren('restaurantListItems', { read: ElementRef })
  restaurantListItems!: QueryList<ElementRef>;

  @ViewChild(RestaurantDetailModalComponent)
  restaurantDetailModalComponent!: RestaurantDetailModalComponent;
  @ViewChild('listScrollContainer')
  listScrollContainer?: ElementRef<HTMLElement>;

  restaurantList: RestaurantDetails[] = [];
  restaurantDetailsList: RestaurantDetails[] = [];
  item: RestaurantDetails = new RestaurantDetails();
  modalViewActive: boolean = false;
  modalRef: NgbModalRef | null = null;
  throttle = 500;
  scrollDistance = 3;
  scrollUpDistance = 5;
  currentPage = 0;
  totalPage = 1;
  resturantCoordinates = [];
  _businessFilter = new BusinessFilter();
  showOpen: boolean = false;
  showClose: boolean = false;
  limit = 20;
  totalRecords: number = 100;
  isVitrualScrollDisabled: boolean = false;
  visibleChipCount = 3;
  hoursTooltipTriggers = tooltipTriggers();
  isFromMobileApp: boolean = false;
  private intersectionObserver: IntersectionObserver;
  subscription!: Subscription;
  @Input() searchIds: any[] = [];
  searchRestaurantList: any;
  isFromDashboard: boolean = false;
  @Input() isCall: boolean = false;
  private requestId = 0;
  private pendingSearchIdsFilter = false;
  private skipNextCenterLoad = false;
  private skipNextLoad = false;
  private ignoreNextFilterReset = false;
  private restoredFilterKey: string | null = null;
  private triedRestoreFromSession = false;
  private restoredFromSession = false;
  private pendingScrollTop: number | null = null;
  private lastListEpicenter: { lat: number; lng: number } | null = null;
  private pendingEpicenterRefresh = false;
  private restaurantCache = new Map<number, RestaurantDetails>();
  private readonly maxCachedRestaurants = 160;
  trackById(index: number, item: any): number {
    return item.id;
  }

  constructor(
    private modalService: NgbModal,
    private loaderService: LoaderService,
    private businessService: BusinessService,
    private route: Router,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {
    // Setup debounced load data
    this.loadDataSubject.pipe(
      takeUntil(this.destroy$),
      debounceTime(300)
    ).subscribe(() => {
      if (this.isCall) {
        this.loadData();
      }
    });
  }

  ngOnInit(): void {
    this.isFromMobileApp = hasNativeLoginBridge(window as any);
    const windowWidth = window.innerWidth;
    if (windowWidth < 768) {
      this.modalViewActive = true;
    } else {
      this.modalViewActive = false;
    }
    if (this.businessService.filterDataObject) {
      this.isFromDashboard = true;
    }
    if (this.businessFilter.origin_Lat == null || this.businessFilter.origin_long == null) {
      this.businessFilter.origin_Lat = '-0.7893';
      this.businessFilter.origin_long = '113.9213';
    }
    this.businessService.onOpenClosedBtnClick
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.showOpen = res.showOpen;
        this.showClose = res.showClose;
        this.scrollTotop();
        this.onCloseOpenResturant();
        this.loadDataSubject.next();
      });

    const closeOpenResturant = this.businessService.getCloseOpenResturant();
    this.showOpen = closeOpenResturant.showOpen;
    this.showClose = closeOpenResturant.showClose;
    const restored = this.restoreListingFromSessionState();
    if (!restored) {
      if (this.showClose || this.showOpen) {
        this.onCloseOpenResturant();
        this.loadDataSubject.next();
      } else if (this.isCall) {
        this.loadDataSubject.next();
      }
    }

    // Setup intersection observer
    this.setupIntersectionObserver();
  }

  private restoreListingFromSessionState(): boolean {
    if (this.restoredFromSession) {
      return true;
    }
    if (this.businessService.filterDataObject && !this.businessService.hasListingReturnPending()) {
      return false;
    }
    const storedState = this.businessService.hydrateListingState();
    const storedFilterData = storedState?.filterDataObject;
    if (!storedState || !this.businessService.shouldRestoreListingState(storedState)) {
      return false;
    }

    const storedList = storedState.restaurantDetailsList;
    const storedMeta = storedState.listMeta;
    const hasStoredList = Array.isArray(storedList);
    const shouldRestoreEmpty = hasStoredList && storedList.length === 0 && typeof storedMeta?.totalRecords === 'number' && storedMeta.totalRecords === 0;
    if (!hasStoredList || (storedList.length === 0 && !shouldRestoreEmpty)) {
      return false;
    }

    this.restaurantDetailsList = storedList;
    this.rebuildRestaurantCache(this.restaurantDetailsList);
    this.restaurantList = Array.isArray(storedState.restaurantList) ? storedState.restaurantList : storedList;
    this.resturantCoordinates = Array.isArray(storedState.resturantCoordinates)
      ? storedState.resturantCoordinates
      : this.buildCoordinatesFromList(this.restaurantDetailsList);

    const meta = storedMeta || {};
    if (typeof meta.currentPage === 'number') {
      this.currentPage = meta.currentPage;
    }
    if (typeof meta.totalPage === 'number') {
      this.totalPage = meta.totalPage;
    }
    if (typeof meta.totalRecords === 'number') {
      this.totalRecords = meta.totalRecords;
    }
    if (typeof meta.limit === 'number') {
      this.limit = meta.limit;
    }
    if (typeof meta.scrollTop === 'number') {
      this.pendingScrollTop = meta.scrollTop;
    }
    if (typeof meta.showOpen === 'boolean') {
      this.showOpen = meta.showOpen;
    } else if (typeof storedFilterData?.showOpen === 'boolean') {
      this.showOpen = storedFilterData.showOpen;
    }
    if (typeof meta.showClose === 'boolean') {
      this.showClose = meta.showClose;
    } else if (typeof storedFilterData?.showClose === 'boolean') {
      this.showClose = storedFilterData.showClose;
    }
    if (storedState.searchRestaurantList) {
      this.searchRestaurantList = storedState.searchRestaurantList;
    }

    if (Array.isArray(this.searchIds) && this.searchIds.length > 0) {
      this.applySearchIdsFilter();
    } else {
      this.onListLoad.emit(this.resturantCoordinates);
      this.businessService.updateRestaurantList(this.resturantCoordinates);
    }

    this.restoredFilterKey = this.buildFilterKey(storedFilterData?.filter, this.showOpen, this.showClose);
    this.lastListEpicenter = this.getFilterEpicenter(storedFilterData?.filter);
    this.skipNextCenterLoad = true;
    this.skipNextLoad = false;
    this.ignoreNextFilterReset = true;
    this.restoredFromSession = true;
    this.cdr.markForCheck();
    this.tryRestoreScrollPosition();
    return true;
  }

  private buildCoordinatesFromList(list: RestaurantDetails[]): any[] {
    return list
      .filter(x => x.coordinate?.lat && x.coordinate?.lng)
      .map((x) => ({
        index: x.index,
        id: x.id,
        lat: x.coordinate.lat,
        lng: x.coordinate.lng,
        restaurantId: x.id,
        listingIndex: x.index,
        absoluteIndex: x.index,
        distance: x.distance,
        zoomLevel: x.zoomLevel,
        name: x.restaurantName
      }));
  }

  private buildFilterKey(filter: BusinessFilter | any, showOpen?: boolean, showClose?: boolean): string {
    if (!filter) return '';
    const lat = filter.origin_Lat != null ? Number(filter.origin_Lat) : null;
    const lng = filter.origin_long != null ? Number(filter.origin_long) : null;
    const normalizeCoord = (value: number | null) =>
      typeof value === 'number' && !Number.isNaN(value) ? value.toFixed(6) : '';

    return JSON.stringify({
      search: filter.search ?? '',
      price: filter.FilterByPriceRange ?? '',
      categories: filter.FilterByCategories ?? '',
      meal: filter.FilterByMealType ?? '',
      lat: normalizeCoord(lat),
      lng: normalizeCoord(lng),
      showOpen: !!showOpen,
      showClose: !!showClose
    });
  }

  private setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = +entry.target?.id;
            if (index >= 0 && this.restaurantDetailsList[index]) {
              this.restaurantDetailsList[index].isVisible = true;
              this.cdr.markForCheck();
            }
          }
        });
      },
      { threshold: 0.1 }
    );
  }

  ngAfterViewInit() {
    this.tryRestoreScrollPosition();
    this.restaurantListItems.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.observeListItems();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    const currentSearchIdsLength = Array.isArray(this.searchIds) ? this.searchIds.length : 0;
    if (changes['searchIds']) {
      if (!changes['searchIds'].firstChange) {
        this.restoredFromSession = false;
      }
    }

    if (this.center && currentSearchIdsLength == 0) {
      if (this.skipNextCenterLoad) {
        this.skipNextCenterLoad = false;
        return;
      }
      if (this.restoredFromSession) {
        this.restoredFromSession = false;
      }
      const centerPoint = this.getCenterPoint(this.center);
      const movementContext = this.getMovementContext(centerPoint);
      this.businessFilter.origin_Lat = this.center.lat;
      this.businessFilter.origin_long = this.center.lng;
      this.businessFilter.north = this.center.north;
      this.businessFilter.south = this.center.south;
      this.businessFilter.east = this.center.east;
      this.businessFilter.west = this.center.west;
      this.businessFilter.zoomLevel = this.center.zoom;
      this.businessFilter.enableZoomFallBack = this.center.enableZoomFallBack;
      if (movementContext.isMeaningfulMove) {
        this.currentPage = 0;
        this.pendingEpicenterRefresh = true;
        this.lastListEpicenter = centerPoint;
        this.restaurantCache = this.pruneRestaurantCache(centerPoint, movementContext.hardRelevanceMeters);
        this.scrollTotop();
      } else if (!this.lastListEpicenter && centerPoint) {
        this.lastListEpicenter = centerPoint;
      }
      if (!movementContext.isMeaningfulMove && this.businessFilter.zoomLevel >= this.restaurantDetailsList[0]?.zoomLevel) {
        this.scrollTotop();
      }
      this.loadData();
    }
    else if (currentSearchIdsLength > 0) {
      if (this.restaurantDetailsList?.length > 0) {
        this.pendingSearchIdsFilter = false;
        this.applySearchIdsFilter();
      } else {
        this.pendingSearchIdsFilter = true;
        if (this.center) {
          this.businessFilter.origin_Lat = this.center.lat;
          this.businessFilter.origin_long = this.center.lng;
          this.businessFilter.north = this.center.north;
          this.businessFilter.south = this.center.south;
          this.businessFilter.east = this.center.east;
          this.businessFilter.west = this.center.west;
          this.businessFilter.zoomLevel = this.center.zoom;
          this.businessFilter.enableZoomFallBack = this.center.enableZoomFallBack;
        }
        if (this.isCall) {
          this.loadData();
        }
      }
    }
  }

  private applySearchIdsFilter() {
    this.restaurantList = this.restaurantDetailsList;
    this.restaurantDetailsList = this.restaurantList.filter(x => this.searchIds.includes(x.id));

    const filteredCoordinates = this.restaurantDetailsList
      .filter(x => x.coordinate.lat && x.coordinate.lng)
      .map((x, i) => ({
        index: i + 1,
        id: x.id,
        lat: x.coordinate.lat,
        lng: x.coordinate.lng,
        restaurantId: x.id,
        listingIndex: i + 1,
        absoluteIndex: i + 1,
        distance: x.distance,
        zoomLevel: x.zoomLevel,
        name: x.restaurantName
      }));

    this.resturantCoordinates = filteredCoordinates;
    this.onListLoad.emit(this.resturantCoordinates);
    this.businessService.updateRestaurantList(this.resturantCoordinates);
    this.persistListingState();
  }

  private observeListItems() {
    if (this.intersectionObserver) {
      this.restaurantListItems.forEach(item => {
        this.intersectionObserver.observe(item.nativeElement);
      });
    }
    this.tryRestoreScrollPosition();
  }

  loadData() {
    if (this.skipNextLoad) {
      this.skipNextLoad = false;
      return;
    }
    const currentRequestId = ++this.requestId;
    const shouldShowBlockingLoader = this.currentPage === 0 && !this.businessFilter.enableZoomFallBack;
    if (shouldShowBlockingLoader) {
      this.loaderService.setLoading(true);
    }

    this.businessFilter.offset = this.currentPage;
    this.businessFilter.sortingType = this.restaurantListType;
    this.businessFilter.limit = this.limit;
    this.businessFilter.timeZoneOffset = new Date().getTimezoneOffset();

    if (this.showOpen || this.showClose) {
      if (this.showOpen)
        this.businessFilter.isOpen = true;
      else
        this.businessFilter.isOpen = false;
    }
    else {
      this.businessFilter.isOpen = null;
    }
    this.businessService.getBusinessList(this.businessFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response && response.records && currentRequestId === this.requestId) {
            if (this.currentPage === 0) {
              if (this.businessFilter.zoomLevel >= this.restaurantDetailsList[0]?.zoomLevel) {
                this.restaurantDetailsList = [];
                if (!this.pendingEpicenterRefresh) {
                  this.restaurantCache.clear();
                }
              }
              this.resturantCoordinates = [];
              this.restaurantList = [];
            }

            this.searchRestaurantList = null;
            if (this.businessFilter?.search != null && this.businessFilter?.search != undefined && this.businessFilter?.search != '') {
              if (this.isFromDashboard) {
                this.processDirectSearchResponse(response);
              } else {
                this.searchRestaurantList = response;
                this.persistListingState();
              }
            } else {
              this.processBusinessResponse(response);
              if (this.pendingSearchIdsFilter || (Array.isArray(this.searchIds) && this.searchIds.length > 0)) {
                this.pendingSearchIdsFilter = false;
                this.applySearchIdsFilter();
              }
            }
            if (this.businessFilter.search) {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
            this.cdr.markForCheck();
            this.isFromDashboard = false;
          }
          if (shouldShowBlockingLoader) {
            this.loaderService.setLoading(false);
          }
        },
        error: () => {
          if (currentRequestId === this.requestId) {
            this.resturantCoordinates = [];
            this.onListLoad.emit(this.resturantCoordinates);
            this.businessService.updateRestaurantList(this.resturantCoordinates);
            this.cdr.markForCheck();
          }
          if (shouldShowBlockingLoader) {
            this.loaderService.setLoading(false);
          }
        }
      });
  }

  private processBusinessResponse(response: any) {
    this.totalRecords = response.total;
    this.totalPage = Math.ceil(response.total / this.businessFilter.limit);

    const newRestaurants = response.records.map((business) =>
      this.mapBusinessToRestaurant(business, 0)
    );
    newRestaurants.forEach(restaurant => this.restaurantCache.set(restaurant.id, restaurant));
    if (this.pendingEpicenterRefresh) {
      const epicenter = this.getFilterEpicenter(this.businessFilter);
      const hardRelevanceMeters = this.getRelevanceThresholdsFromFilter().hard;
      this.restaurantCache = this.pruneRestaurantCache(epicenter, hardRelevanceMeters);
      this.pendingEpicenterRefresh = false;
    }
    this.restaurantDetailsList = this.getRankedRestaurantsForCurrentContext();
    this.restaurantList = this.restaurantDetailsList;
    const newCoordinates = this.restaurantDetailsList
      .filter(x => x.coordinate.lat && x.coordinate.lng)
      .map(x => ({
        index: x.index,
        id: x.id,
        lat: x.coordinate.lat,
        lng: x.coordinate.lng,
        restaurantId: x.id,
        listingIndex: x.index,  // This is now the absolute index
        absoluteIndex: x.index, // Adding this for clarity
        distance: x.distance,
        zoomLevel: x.zoomLevel,
        name: x.restaurantName
      }));

    this.resturantCoordinates = newCoordinates;
    this.onListLoad.emit(this.resturantCoordinates);

    // Update map markers
    this.businessService.updateRestaurantList(this.resturantCoordinates);
    this.persistListingState();
  }

  private processDirectSearchResponse(response: any) {
    this.totalRecords = response.total;
    this.totalPage = Math.ceil(response.total / this.businessFilter.limit);

    const restaurants = response.records.map((business, index) => {
      const restaurant = this.mapBusinessToRestaurant(business, index + 1);
      restaurant.zoomLevel = this.businessFilter.zoomLevel;
      restaurant.isVisible = true;
      return restaurant;
    });

    this.restaurantDetailsList = restaurants;
    this.restaurantList = restaurants;
    this.resturantCoordinates = this.buildCoordinatesFromList(restaurants);
    this.onListLoad.emit(this.resturantCoordinates);
    this.businessService.updateRestaurantList(this.resturantCoordinates);
    this.persistListingState();
  }

  private rebuildRestaurantCache(restaurants: RestaurantDetails[]) {
    this.restaurantCache.clear();
    restaurants.forEach(restaurant => this.restaurantCache.set(restaurant.id, restaurant));
  }

  private getRankedRestaurantsForCurrentContext(): RestaurantDetails[] {
    const epicenter = this.getFilterEpicenter(this.businessFilter);
    const hardRelevanceMeters = this.getRelevanceThresholdsFromFilter().hard;
    const restaurants = Array.from(this.restaurantCache.values())
      .filter(restaurant => this.isRestaurantRelevantToEpicenter(restaurant, epicenter, hardRelevanceMeters))
      .map(restaurant => ({
        ...restaurant,
        distance: epicenter ? this.getDistanceMeters(epicenter, this.getRestaurantPoint(restaurant)) : restaurant.distance,
        zoomLevel: this.businessFilter.zoomLevel,
        isVisible: true
      }))
      .sort((a, b) => (a.distance ?? Number.MAX_SAFE_INTEGER) - (b.distance ?? Number.MAX_SAFE_INTEGER))
      .slice(0, this.maxCachedRestaurants)
      .map((restaurant, index) => ({
        ...restaurant,
        index: index + 1
      }));

    this.rebuildRestaurantCache(restaurants);
    return restaurants;
  }

  private getMovementContext(nextEpicenter: { lat: number; lng: number } | null) {
    const thresholds = this.getRelevanceThresholdsFromCenter(this.center);
    if (!nextEpicenter || !this.lastListEpicenter) {
      return { isMeaningfulMove: false, distanceMeters: 0, hardRelevanceMeters: thresholds.hard };
    }
    const distanceMeters = this.getDistanceMeters(this.lastListEpicenter, nextEpicenter);
    return {
      isMeaningfulMove: distanceMeters > thresholds.refresh,
      distanceMeters,
      hardRelevanceMeters: thresholds.hard
    };
  }

  private getRelevanceThresholdsFromFilter() {
    return this.getRelevanceThresholdsFromCenter(this.businessFilter);
  }

  private getRelevanceThresholdsFromCenter(center: any) {
    const dims = this.getViewportDimsMeters(center);
    const shorterRadius = Math.min(dims.width, dims.height) / 2;
    const refresh = this.clamp(shorterRadius * 0.30, 120, 2500);
    const soft = this.clamp(shorterRadius, 500, 6000);
    const hard = this.clamp(shorterRadius * 1.5, 1000, 10000);
    return { refresh, soft, hard };
  }

  private getViewportDimsMeters(center: any) {
    const north = Number(center?.north);
    const south = Number(center?.south);
    const east = Number(center?.east);
    const west = Number(center?.west);
    if ([north, south, east, west].some(value => Number.isNaN(value))) {
      return { width: 0, height: 0 };
    }
    const midLat = (north + south) / 2;
    return {
      width: this.getDistanceMeters({ lat: midLat, lng: west }, { lat: midLat, lng: east }),
      height: this.getDistanceMeters({ lat: south, lng: west }, { lat: north, lng: west })
    };
  }

  private pruneRestaurantCache(epicenter: { lat: number; lng: number } | null, hardRelevanceMeters: number) {
    if (!epicenter) {
      return this.restaurantCache;
    }
    const nextCache = new Map<number, RestaurantDetails>();
    Array.from(this.restaurantCache.values())
      .filter(restaurant => this.isRestaurantRelevantToEpicenter(restaurant, epicenter, hardRelevanceMeters))
      .sort((a, b) => this.getDistanceMeters(epicenter, this.getRestaurantPoint(a)) - this.getDistanceMeters(epicenter, this.getRestaurantPoint(b)))
      .slice(0, this.maxCachedRestaurants)
      .forEach(restaurant => nextCache.set(restaurant.id, restaurant));
    return nextCache;
  }

  private isRestaurantRelevantToEpicenter(restaurant: RestaurantDetails, epicenter: { lat: number; lng: number } | null, hardRelevanceMeters: number) {
    if (!epicenter) {
      return true;
    }
    const point = this.getRestaurantPoint(restaurant);
    if (!point) {
      return false;
    }
    return this.getDistanceMeters(epicenter, point) <= hardRelevanceMeters;
  }

  private getFilterEpicenter(filter: any): { lat: number; lng: number } | null {
    return this.getCenterPoint({
      lat: filter?.origin_Lat,
      lng: filter?.origin_long
    });
  }

  private getCenterPoint(center: any): { lat: number; lng: number } | null {
    const lat = Number(center?.lat);
    const lng = Number(center?.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }
    return { lat, lng };
  }

  private getRestaurantPoint(restaurant: RestaurantDetails): { lat: number; lng: number } | null {
    const lat = Number(restaurant?.coordinate?.lat);
    const lng = Number(restaurant?.coordinate?.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }
    return { lat, lng };
  }

  private getDistanceMeters(from: { lat: number; lng: number }, to: { lat: number; lng: number } | null): number {
    if (!to) {
      return Number.MAX_SAFE_INTEGER;
    }
    const toRadians = (degrees: number) => degrees * Math.PI / 180;
    const earthRadiusMeters = 6371000;
    const deltaLat = toRadians(to.lat - from.lat);
    const deltaLng = toRadians(to.lng - from.lng);
    const lat1 = toRadians(from.lat);
    const lat2 = toRadians(to.lat);
    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) *
      Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusMeters * c;
  }

  private clamp(value: number, min: number, max: number) {
    return Math.max(min, Math.min(max, value));
  }

  private persistListingState() {
    this.businessService.saveListingState({
      restaurantDetailsList: this.restaurantDetailsList,
      restaurantList: this.restaurantList,
      resturantCoordinates: this.resturantCoordinates,
      searchRestaurantList: this.searchRestaurantList,
      listMeta: {
        currentPage: this.currentPage,
        totalPage: this.totalPage,
        totalRecords: this.totalRecords,
        limit: this.limit,
        restaurantListType: this.restaurantListType,
        showOpen: this.showOpen,
        showClose: this.showClose,
        scrollTop: this.listScrollContainer?.nativeElement?.scrollTop ?? 0
      }
    });
  }

  private tryRestoreScrollPosition() {
    if (this.pendingScrollTop == null || !this.listScrollContainer?.nativeElement) {
      return;
    }

    const scrollTop = this.pendingScrollTop;
    requestAnimationFrame(() => {
      if (!this.listScrollContainer?.nativeElement) {
        return;
      }
      this.listScrollContainer.nativeElement.scrollTop = scrollTop;
      this.pendingScrollTop = null;
    });
  }

  private mapBusinessToRestaurant(business: any, index: number): RestaurantDetails {
    // Process business hours
    if (business.membershipAppCompanyBusinessHourList?.length) {
      business.membershipAppCompanyBusinessHourList =
        business.membershipAppCompanyBusinessHourList.map(hour => {
          if (hour.from !== "" || hour.to !== "") {
            hour.from = hour.from.length === 7 ? "0" + hour.from : hour.from;
            hour.to = hour.to.length === 7 ? "0" + hour.to : hour.to;
          }
          return hour;
        });
    }

    const restaurant = {
      index: index,
      id: business.branchId,
      photo: business?.membershipBusinessImagesList?.find(x => x.imageType === 1)?.imagePath,
      restaurantName: business.name,
      typeName: business.typeName,
      status: business.isOpen ? 'Open Now' : 'Closed Now',
      cuisineName: business.cuisineName,
      priceRange: business.priceRange,
      cityName: business.cityName,
      address: business.address,
      rank: null,
      rating: null,
      openWeeksHours: businessHours.groupBySameTiming(business.membershipAppCompanyBusinessHourList),
      membershipAppCompanyBusinessHourList: business?.membershipAppCompanyBusinessHourList,
      coordinate: {
        lat: business.lat,
        lng: business.lng
      },
      showMore: false,
      tags: this.generateTags(business),
      toolTipHours: business.membershipAppCompanyBusinessHourList?.filter(hour =>
        hour.from !== "" || hour.to !== ""
      ) || [],
      distance: business.distance,
      memberShipImageConfigurationList: business?.membershipBusinessImagesList?.find(
        (x) => x.imageType == 1)?.memberShipImageConfigurationViews?.find((y) => y.imageType == 2),
      zoomLevel: business.zoomLevel,
      directionsUrl: directionsUrl(business.lat, business.lng, business.address)
    } as RestaurantDetails;

    const image = business?.membershipBusinessImagesList?.find(x => x.imageType === 1)?.imagePath;
    const imageConfigurationDetails = business?.membershipBusinessImagesList?.find(
      (x) => x.imageType == 1)?.memberShipImageConfigurationViews?.find((y) => y.imageType == 2);
    if (imageConfigurationDetails) {
      this.subscription = this.cropImage(image, imageConfigurationDetails?.x, imageConfigurationDetails?.y,
        imageConfigurationDetails?.width, imageConfigurationDetails?.height)
        .subscribe({
          next: (cropped) => {
            restaurant.photo = cropped
          },
          error: (err) => console.log("failed")
        });
    }

    return restaurant;
  }

  private generateTags(business: any): string[] {
    const tags = [];
    if (business.typeName) tags.push(business.typeName);
    if (business.cuisineName) tags.push(business.cuisineName);
    if (business.priceRange) tags.push(business.priceRange);
    if (business.rank) tags.push(business.rank);
    return tags;
  }

  onScrollDown() {
    if (this.totalPage && (this.currentPage + 1) < this.totalPage) {
      this.currentPage += 1;
      this.loadDataSubject.next();
    }
  }

  ngOnDestroy() {
    this.persistListingState();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.destroy$.next();
    this.destroy$.complete();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onCloseOpenResturant() {
    this.currentPage = 0;
  }

  scrollTotop() {
    let myDiv = document.querySelector('.restro-details-wrap');
    if (myDiv != null) {
      myDiv.scrollTop = 0;
    }
  }

  getDayName(day: number): string {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return dayNames[day];
  }

  /**
   * Reveals the hidden tag chips. Idempotent on purpose: desktop reaches it via
   * mouseover and touch/keyboard via click, and a tap fires both, so a toggle
   * would open and immediately close. The click must not bubble to the card,
   * which would open the preview modal.
   */
  showMoreTags(item, event?: Event) {
    event?.stopPropagation();
    item.showMore = true;
  }

  get allChipsVisible() {
    return !this.item.showMore;
  }

  // Modal Code

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    const windowWidth = window.innerWidth;
    if (windowWidth < 768) {
      this.modalViewActive = true;
    } else {
      this.modalViewActive = false;
      this.closeRestroContentModal();
    }
  }

  openRestroContentModal(
    restroContent: TemplateRef<any>,
    item: RestaurantDetails
  ) {
    localStorage.setItem("selectedBranchId", item?.id.toString());
    this.persistListingState();
    if (this.modalViewActive) {
      this.item = item;
      this.modalRef = this.modalService.open(restroContent, {
        windowClass: 'project-modal restro-details-modal',
        centered: true,
        backdropClass: 'light-backdrop',
      });
    }
    else {
      this.businessService.markListingReturnPending();
      this.route.navigate(['business-detail', item.id]);
    }
  }

  closeRestroContentModal() {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }
    this.businessService.clearListingReturnPending();
  }

  onRestroScroll() {
    const div = this.elementRef.nativeElement.querySelector(
      '.restro-details-wrap'
    );
    this.renderer.addClass(div, 'scroll-active');
    setTimeout(() => {
      this.renderer.removeClass(div, 'scroll-active');
    }, 1500);
  }

  getCoordinates($event) {
    this.businessService.emitLocations($event);
  }

  resetMarkerIcon($event) {
    this.businessService.resetIcon($event);
  }

  hideSearchBar() {
    this.businessService.onScrollChange.emit();
  }

  cropImage(imageSrc: string, x: number, y: number, width: number, height: number): Observable<string> {
    return new Observable(observer => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.log("ctx error");
        return;
      }

      const img = new Image();
      if (imageSrc != null || imageSrc != undefined) {
        img.crossOrigin = "anonymous";
        img.src = imageSrc;

        img.onload = () => {
          canvas.width = 226;
          canvas.height = 156;

          ctx.drawImage(
            img,
            x, y,
            width, height,
            0, 0,
            226, 156
          );

          const croppedData = canvas.toDataURL("image/png");
          observer.next(croppedData);
          observer.complete();
        }

        img.onerror = () => {
          img.src = imageSrc;
        }
      }
      else {
        img.src = imageSrc;
      }
    });
  }

  onExpandList() {
    this.businessService.expandList(true);
  }

  onExpandSearch() {
    this.processDirectSearchResponse(this.searchRestaurantList);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
